import { Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { VisitorRegistrationFormHeader } from '../../models/visitor-registration-form-header';
import { VisitorRegistrationFormType } from '../../models/visitor-registration-form-type';
import { VisitorRegistrationForm } from '../../models/visitor-registration-form';
import { environment } from '../../../environments/environment';
import { BaseApiService } from './base-api-service.service';
import { CreateFormResponse } from '../../models/create-form-response';
import { CopyFormResponse } from '../../models/copy-form-response.model';
import { ApproveCondition } from '../../models/approve-condition.model';
import { ReviewType } from './review-type';

@Injectable({
  providedIn: 'root'
})
export class RegistrationFormService extends BaseApiService {
  
  createForm(formType: VisitorRegistrationFormType): Observable<CreateFormResponse> {
    let createData = { formType: formType };
    return this.httpClient.post<CreateFormResponse>(`${environment.apiBaseUrl}/forms`, createData).pipe(
      catchError(this.handleError<CreateFormResponse>('createForm', null))
    );
  }

  copyForm(formId: string): Observable<CopyFormResponse> {
    return this.httpClient.post<CopyFormResponse>(`${environment.apiBaseUrl}/forms/${formId}/copy`, null).pipe(
      catchError(this.handleError<CopyFormResponse>('copyForm', null))
    );
  }

  getForm(formId: string): Observable<VisitorRegistrationForm> {
    return this.httpClient.get<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}`).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('getForm', null))
    );
  }

  updateForm(formId: string, data: VisitorRegistrationForm): Observable<VisitorRegistrationForm> {
    return this.httpClient.put<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}`, data).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('updateForm', null))
    );
  }

  deleteForm(formId: string): Observable<Object> {
    return this.httpClient.delete(`${environment.apiBaseUrl}/forms/${formId}`).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('deleteForm', null))
    );
  }

  submitForm(formId: string, data: VisitorRegistrationForm): Observable<VisitorRegistrationForm> {
    return this.httpClient.post<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}/submit`, data).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('submitForm', null))
    );
  }

  withdrawForm(formId: string): Observable<VisitorRegistrationForm> {
    return this.httpClient.post<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}/withdraw`, null).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('withdrawForm', null))
    );
  }

  rejectForm(formId: string, reviewType: ReviewType, rejectionReason: string): Observable<VisitorRegistrationForm> {
    let data = { reason: rejectionReason };
    return this.httpClient.post<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}/reject?reviewType=${reviewType}`, data).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('rejectForm', null))
    );
  }

  approveForm(formId: string, reviewType: ReviewType): Observable<VisitorRegistrationForm> {
    return this.httpClient.post<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}/approve?reviewType=${reviewType}`, null).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('approveForm', null))
    );
  }

  approveConditionally(formId: string, reviewType: ReviewType, approveCondition: ApproveCondition) {
    return this.httpClient.post<VisitorRegistrationForm>(`${environment.apiBaseUrl}/forms/${formId}/approveConditionally?reviewType=${reviewType}`, approveCondition).pipe(
      catchError(this.handleError<VisitorRegistrationForm>('approveFormToReview', null))
    );
  }

  getUserActiveForms(): Observable<VisitorRegistrationFormHeader[]> {
    return this.httpClient.get<VisitorRegistrationFormHeader[]>(`${environment.apiBaseUrl}/forms/`).pipe(
      catchError(this.handleError<VisitorRegistrationFormHeader[]>('getUserActiveForms', []))
    );
  }

  getFormsForHRReview(): Observable<VisitorRegistrationFormHeader[]> {
    return this.httpClient.get<VisitorRegistrationFormHeader[]>(`${environment.apiBaseUrl}/forms?filter=forHRReview`).pipe(
      catchError(this.handleError<VisitorRegistrationFormHeader[]>('getFormsForHRReview', []))
    );
  }

  getFormsForEHSReview(): Observable<VisitorRegistrationFormHeader[]> {
    return this.httpClient.get<VisitorRegistrationFormHeader[]>(`${environment.apiBaseUrl}/forms?filter=forEHSReview`).pipe(
      catchError(this.handleError<VisitorRegistrationFormHeader[]>('getFormsForEHSReview', []))
    );
  }

  getApprovedForms(): Observable<VisitorRegistrationFormHeader[]> {
    return this.httpClient.get<VisitorRegistrationFormHeader[]>(`${environment.apiBaseUrl}/forms?filter=approved`).pipe(
      catchError(this.handleError<VisitorRegistrationFormHeader[]>('getApprovedForms', []))
    );
  }

  getInProgressForms(): Observable<VisitorRegistrationFormHeader[]> {
    return this.httpClient.get<VisitorRegistrationFormHeader[]>(`${environment.apiBaseUrl}/forms?filter=inProgress`).pipe(
      catchError(this.handleError<VisitorRegistrationFormHeader[]>('getInProgressForms', []))
    );
  }
}
